import { useEffect, useId, useMemo, useState } from "react";
import axios from "../../api/axiosInstance";
import { usePmsTheme } from "../../Components/PmsThemeContext";
import useIsMobile from "../../hooks/useIsMobile";
import { directConsumptionPresentation } from "./directConsumptionMessages";
import ItemMovementHistory from "./ItemMovementHistory";
import usePurchasingReadAccess from "./usePurchasingReadAccess";
import "./DirectConsumptionPurchases.css";

export function defaultPurchaseDates(today = new Date()) {
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  return { start_date: `${year}-${month}-01`, end_date: `${year}-${month}-${String(today.getDate()).padStart(2, "0")}`, search: "" };
}

const emptyResult = { rows: [], pagination: {}, summary: {} };
const hierarchy = (row) => [row.family, row.subfamily, row.category, row.subcategory].map((level) => level?.name).filter(Boolean).join(" / ");
const locationKey = (location) => location?.id == null ? "unallocated" : String(location.id);

export default function DirectConsumptionPurchases({ businessUnitId, canView: viewAllowed = true, refreshToken = 0 }) {
  const { languageId } = usePmsTheme();
  const { can, requestConfig, contextKey } = usePurchasingReadAccess(businessUnitId);
  const canView = viewAllowed && can("purchasing.stock.current");
  const canViewMovements = can("purchasing.stock.current.movements");
  const { t, formatDate, formatOptionalDecimal } = useMemo(() => directConsumptionPresentation(languageId), [languageId]);
  const isMobile = useIsMobile();
  const fieldId = useId();
  const [draft, setDraft] = useState(defaultPurchaseDates);
  const [filters, setFilters] = useState(defaultPurchaseDates);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [retry, setRetry] = useState(0);
  const [validationError, setValidationError] = useState("");
  const [response, setResponse] = useState(null);
  const [view, setView] = useState("global");
  const [selectedItem, setSelectedItem] = useState(null);
  const requestKey = JSON.stringify([contextKey, businessUnitId, canView, filters, page, pageSize, retry, refreshToken]);
  const currentResponse = response?.key === requestKey ? response : null;
  const loading = Boolean(businessUnitId && canView && (!currentResponse || currentResponse.loading));
  const result = currentResponse?.data || emptyResult;
  const rows = Array.isArray(result.rows) ? result.rows : [];
  const pagination = result.pagination || {};
  const total = Number(pagination.total_count || 0);
  const totalPages = Math.max(1, Number(pagination.total_pages || 1));
  const summary = result.summary || {};
  const locations = Array.isArray(result.locations) ? result.locations : [];

  useEffect(() => {
    if (!canView || !can("purchasing.stock.current", true)) return undefined;
    let cancelled = false;
    const controller = new AbortController();
    setResponse({ key: requestKey, loading: true });
    axios.get("/api/purchasing/inventory/direct-consumption", {
      ...requestConfig("purchasing.stock.current"),
      params: { business_unit_id: businessUnitId, ...filters, page, page_size: pageSize, view: "items" },
      signal: controller.signal,
    }).then(({ data }) => {
      if (!cancelled && can("purchasing.stock.current", true)) setResponse({ key: requestKey, data, loading: false });
    }).catch(() => {
      if (!cancelled && can("purchasing.stock.current", true)) setResponse({ key: requestKey, error: true, loading: false });
    });
    return () => { cancelled = true; controller.abort(); };
  }, [businessUnitId, canView, filters, page, pageSize, requestKey, can, requestConfig]);

  useEffect(() => { setSelectedItem(null); }, [contextKey]);

  const money = (value, currency) => {
    if (value == null || !Number.isFinite(Number(value))) return "-";
    return new Intl.NumberFormat(languageId === "es" ? "es-ES" : "en-GB", {
      ...(/^[A-Z]{3}$/.test(currency || "") ? { style: "currency", currency } : {}),
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    }).format(Number(value));
  };
  const purchaseDate = (value) => formatDate(/^\d{4}-\d{2}-\d{2}$/.test(value || "") ? `${value}T12:00:00` : value);
  const purchaseValue = Array.isArray(summary.totals_by_currency) && summary.totals_by_currency.length
    ? summary.totals_by_currency.map((entry) => money(entry.total_value, entry.currency)).join(" · ")
    : summary.total_value == null ? t("Multiple currencies") : money(summary.total_value, summary.currency);
  const submit = (event) => {
    event.preventDefault();
    if (!draft.start_date || !draft.end_date || draft.start_date > draft.end_date) {
      setValidationError("Choose a start date on or before the end date.");
      return;
    }
    setValidationError("");
    setFilters({ ...draft, search: draft.search.trim() });
    setPage(1);
    setSelectedItem(null);
    setRetry((value) => value + 1);
  };
  const change = (event) => setDraft((current) => ({ ...current, [event.target.name]: event.target.value }));
  const quantity = (entries = []) => <span className="sm-directFigures">
    {entries.length ? entries.map((entry, index) => <span key={entry.unit?.id ?? index}>
      {formatOptionalDecimal(entry.quantity)} <small>{entry.unit?.name || entry.unit?.code || t("Unit not set")}</small>
    </span>) : <span className="sm-directEmptyQuantity">-</span>}
  </span>;
  const values = (entries = []) => <span className="sm-directFigures">
    {entries.length ? entries.map((entry) => <span key={entry.currency}>{money(entry.total_value, entry.currency)}</span>) : "-"}
  </span>;
  const costs = (entries = []) => <span className="sm-directFigures">
    {entries.length ? entries.map((entry, index) => <span key={index}>{money(entry.unit_cost, entry.currency)}
      <small> / {entry.unit?.name || entry.unit?.code || t("Unit not set")}</small>
    </span>) : "-"}
  </span>;
  const locationTotal = (row, location) => {
    const entry = row.locations?.find((value) => locationKey(value.location) === locationKey(location));
    return entry ? <span className="sm-directLocationTotals">
      {quantity(entry.quantities)}
      <small>{values(entry.totals_by_currency)}</small>
    </span> : <span className="sm-directEmptyQuantity">-</span>;
  };
  const openMovements = (row) => can("purchasing.stock.current.movements", true) && setSelectedItem({ businessUnitId, row: {
    itemId: row.item_id, itemCode: row.item_code, itemName: row.item_name,
    familyName: row.family?.name, subfamilyName: row.subfamily?.name,
    categoryName: row.category?.name, subcategoryName: row.subcategory?.name,
  } });
  const movementsButton = (row) => <button type="button" className="sm-actionBtn sm-directMovements"
    disabled={!canViewMovements} aria-label={t("View movements for {0}", { 0: row.item_name })} onClick={() => openMovements(row)}>
    {t("View movements")}
  </button>;

  if (!canView) return <div className="sm-empty" role="alert">{t("You don't have permission to access this section.")}</div>;
  if (!businessUnitId) return <div className="sm-empty" role="status">{t("Select a business unit to view inventory.")}</div>;

  return <div className="sm-directPurchases" aria-label={t("Direct Consumption purchases")} aria-busy={loading}>
    <p className="sm-directDescription">{t("Purchases grouped by item for the selected dates. Open movements to review the delivery notes.")}</p>
    <form className="sm-directFilters" onSubmit={submit}>
      <label htmlFor={`${fieldId}-from`}>{t("From date")}
        <input id={`${fieldId}-from`} type="date" name="start_date" value={draft.start_date} onChange={change} required />
      </label>
      <label htmlFor={`${fieldId}-to`}>{t("To date")}
        <input id={`${fieldId}-to`} type="date" name="end_date" value={draft.end_date} onChange={change} required />
      </label>
      <label className="sm-directSearch" htmlFor={`${fieldId}-search`}>{t("Search purchases")}
        <input id={`${fieldId}-search`} type="search" name="search" maxLength={200} value={draft.search} onChange={change}
          placeholder={t("Search by item, code, supplier, document or classification...")} />
      </label>
      <button type="submit" className="sm-actionBtn">{t("Show purchases")}</button>
      <div className="sm-directViews" role="group" aria-label={t("Consumption view")}>
        {[["global", "Global"], ["locations", "By Location"]].map(([key, label]) => <button
          key={key} type="button" aria-pressed={view === key}
          className={`sm-viewTab ${view === key ? "sm-viewTab--active" : ""}`} onClick={() => setView(key)}>
          {t(label)}
        </button>)}
      </div>
    </form>
    {validationError && <div className="sm-error" role="alert">{t(validationError)}</div>}
    {currentResponse?.error && <div className="sm-error" role="alert">
      <span>{t("Could not load direct-consumption purchases.")}</span>
      <button type="button" className="sm-actionBtn" onClick={() => setRetry((value) => value + 1)}>{t("Retry")}</button>
    </div>}
    {!loading && !currentResponse?.error && <>
      {result.location_access_scope === "user_locations" && <p className="sm-directDescription">{t("Only purchases from your authorized locations are shown.")}</p>}
      <div className="sm-stats">
        {[["Distinct items", summary.item_count ?? total], ["Purchase lines", summary.line_count ?? 0], ["Delivery notes", summary.purchase_count ?? 0], ["Purchase Value", purchaseValue]].map(([label, value]) =>
          <div className="sm-stat" key={label}><span className="sm-statLabel">{t(label)}</span><span className="sm-statValue">{value}</span></div>)}
      </div>
    </>}
    <div className="sm-cardBody">
      {loading ? <div className="sm-empty" role="status">{t("Loading direct-consumption purchases...")}</div>
        : currentResponse?.error ? null
        : !rows.length ? <div className="sm-empty" role="status">{t("No direct-consumption purchases match these dates and filters.")}</div>
        : isMobile ? <div className="sm-mobileList">
          {rows.map((row) => <article key={row.id} className="sm-mobileCard">
            <div className="sm-code">{row.item_code || "-"}</div>
            <div className="sm-itemName">{row.item_name || "-"}</div>
            <div className="sm-hierarchy">{hierarchy(row)}</div>
            <div className="sm-mobileGrid">
              {[["Consumed quantity", quantity(row.quantities)], ["Weighted unit cost", costs(row.unit_costs)],
                ["Purchase Value", values(row.totals_by_currency)], ["Last purchase", purchaseDate(row.last_date)]].map(([label, value]) => <div key={label}>
                  <div className="sm-mobileLabel">{t(label)}</div><div className="sm-mobileValue">{value}</div>
                </div>)}
            </div>
            {view === "locations" && <div className="sm-mobileLocationList">
              {locations.map((location) => <div key={locationKey(location)} className="sm-mobileLocationRow">
                <span>{location?.name || t("Unassigned location")}</span>{locationTotal(row, location)}
              </div>)}
            </div>}
            <div className="sm-directMobileActions">{movementsButton(row)}</div>
          </article>)}
        </div> : <div className="sm-tableWrap">
          <table className="sm-table sm-table--directPurchases" aria-label={t("Direct Consumption purchases")}>
            <thead><tr>
              <th scope="col">{t("Item")}</th><th scope="col">{t("Code")}</th>
              <th scope="col" className="sm-num">{t("Consumed quantity")}</th>
              {view === "locations" ? locations.map((location) => <th key={locationKey(location)} scope="col" className="sm-num sm-directLocationCell">
                {location?.name || t("Unassigned location")}
              </th>) : <th scope="col" className="sm-num">{t("Weighted unit cost")}</th>}
              <th scope="col" className="sm-num">{t("Purchase Value")}</th><th scope="col">{t("Last purchase")}</th>
              {["Family", "Subfamily", "Category", "Subcategory", "Movements"].map((label) => <th key={label} scope="col">{t(label)}</th>)}
            </tr></thead>
            <tbody>{rows.map((row) => <tr key={row.id}>
              <td className="sm-cellStrong">{row.item_name || "-"}</td><td className="sm-code">{row.item_code || "-"}</td>
              <td className="sm-num">{quantity(row.quantities)}</td>
              {view === "locations" ? locations.map((location) => <td key={locationKey(location)} className="sm-num sm-directLocationCell">
                {locationTotal(row, location)}
              </td>) : <td className="sm-num">{costs(row.unit_costs)}</td>}
              <td className="sm-num">{values(row.totals_by_currency)}</td><td>{purchaseDate(row.last_date)}</td>
              {["family", "subfamily", "category", "subcategory"].map((level) => <td className="sm-cellMuted" key={level}>{row[level]?.name || "-"}</td>)}
              <td>{movementsButton(row)}</td>
            </tr>)}</tbody>
          </table>
        </div>}
    </div>
    {!currentResponse?.error && <div className="sm-pagination">
      <span aria-live="polite">{loading ? t("Loading…") : t("{0}–{1} of {2} items", {
        0: rows.length ? (page - 1) * pageSize + 1 : 0, 1: rows.length ? (page - 1) * pageSize + rows.length : 0, 2: total,
      })}</span>
      <div className="sm-paginationControls">
        <label htmlFor={`${fieldId}-page-size`}>{t("Rows per page")}</label>
        <select id={`${fieldId}-page-size`} value={pageSize} disabled={loading} onChange={(event) => { setPageSize(Number(event.target.value)); setPage(1); }}>
          {[50, 100, 200].map((size) => <option key={size} value={size}>{size}</option>)}
        </select>
        <button type="button" disabled={loading || page <= 1} onClick={() => setPage((value) => value - 1)}>{t("Previous")}</button>
        <span>{t("Page {0} of {1}", { 0: page, 1: totalPages })}</span>
        <button type="button" disabled={loading || page >= totalPages} onClick={() => setPage((value) => value + 1)}>{t("Next")}</button>
      </div>
    </div>}
    {canViewMovements && selectedItem && String(selectedItem.businessUnitId) === String(businessUnitId) && <ItemMovementHistory
      row={selectedItem.row} businessUnitId={businessUnitId} onClose={() => setSelectedItem(null)}
      onDataChanged={() => setRetry((value) => value + 1)}
    />}
  </div>;
}
